import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useReducer, useState } from "react";

import { useParams } from "react-router-dom";

import {
  getProductById,
  updateProduct,
  clearMessage,
  patchProduct,
  fetchProductListingPages,
} from "@/services/productServices/productActions";
import ConfirmDelete from "@/components/ConfirmDelete";
import ListingPagesAffected from "./ListingPagesAffected";
import { getClients } from "@/services/clientService/clientActions";
import { useImageUpload } from "@/services/ImageServices/useImageUpload";
import {
  getEntityPendingChange,
  clearEntityPendingChange,
} from "@/services/pendingChangesServices/pendingChangesActions";

import { COUNTRY_OPTIONS } from "@/utils/selectOptions";
import NotificationAlertOptions from "@/layouts/Alerts";

import UpdateProductView from "./UpdateProductView";
import { clientOptions, productForm } from "./productForm";
import { asCaught } from "@/services/caughtError";
import getErrorMessage, { DEFAULT_ERROR_MESSAGE } from "@/utils/getErrorMessage";

/**
 * The toast text for a failed save (SCRUM-2020): the reason from the response
 * body in any shape getErrorMessage reads (the server contract's
 * `{ errors: [{ msg }] }` first), or the action's own fallback when the body
 * carries nothing readable. Only an object body is read, so a proxy's HTML
 * error page never lands in a toast.
 */
const formActions = productForm.actions;

const failureMessage = (err, fallback) => {
  const body = asCaught(err)?.response?.data;
  if (!body || typeof body !== "object") return fallback;
  const message = getErrorMessage(body);
  return message === DEFAULT_ERROR_MESSAGE ? fallback : message;
};

const UpdateProductContainer = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const uploadImage = useImageUpload();

  // SCRUM-771: per-slice selectors so unrelated dispatches don't re-render this
  // container (an object-literal selector returns a new reference every render).
  const productFromStore = useAppSelector((s) => s.products.product_byId);
  const productsSlice = useAppSelector((s) => s.products);
  const errors = useAppSelector((s) => s.errors);
  const clients = useAppSelector((s) => s.clients.clients);
  const currentSite = useAppSelector((s) => s.sites.site);
  const entityPendingChange = useAppSelector((s) => s.pendingChanges.entityPendingChange);

  // SCRUM-2264: the product draft is the shared productForm createSlice, mounted
  // locally. The client and the image are read off the working copy, which always
  // carried the same values; the client options are computed in render.
  const [form, dispatchForm] = useReducer(productForm.reducer, productForm.getInitialState());
  const { title, alt_pic, sponsored, rating } = form;
  const product = form.product || {};
  const productImg = product.productImg;
  const selectedClientId = product.client || null;
  const clientsOptions = clientOptions(clients, "first");

  const [loading, setLoading] = useState(false);
  const [pendingChangeReady, setPendingChangeReady] = useState(false);
  // SCRUM-1303: the unlink confirmation, naming the listing pages that change.
  const [alert, setAlert] = useState<React.ReactNode>(null);

  useEffect(() => {
    if (id) {
      dispatch(getProductById({ id }));
      dispatch(getEntityPendingChange("product", id))
        .finally(() => setPendingChangeReady(true));
    }
    dispatch(getClients());

    return () => {
      dispatch(clearEntityPendingChange());
      setPendingChangeReady(false);
    };
  }, [dispatch, id]);

  // Fill the form from the product read; productLoaded ignores it once a product is
  // loaded (first load wins, SCRUM-2262).
  useEffect(() => {
    if (productFromStore?._id) dispatchForm(formActions.productLoaded(productFromStore));
  }, [productFromStore]);

  // Server errors: stop the spinner. Adjusting state while rendering
  // (React: "storing information from previous renders") instead of an effect that only
  // copies the store value into state (S9 mirror-effect, SCRUM-2254).
  const [seenErrors, setSeenErrors] = useState<typeof errors | null>(null);
  if (errors !== seenErrors) {
    setSeenErrors(errors);
    if (errors?.errors?.length) {
      setLoading(false);
    }
  }

  // success message -> notify + refresh both product and pending change
  useEffect(() => {
    const msg = productsSlice?.message;
    if (msg) {
      NotificationAlertOptions("success", msg);
      setLoading(false);
      dispatch(clearMessage());
      if (id) {
        dispatch(getProductById({ id }));
        dispatch(getEntityPendingChange("product", id));
      }
    }
  }, [productsSlice?.message, dispatch, id]);

  
  const handleAltChange = (e) => {
    dispatchForm(formActions.productEdited({ alt_pic: e.target.value }));
  };

  const handleTitleChange = (e) => {
    dispatchForm(formActions.productEdited({ title: e.target.value }));
  };

  const handleCountryChange = (opt) => {
    dispatchForm(formActions.productEdited({ country_code: opt?.value || "" }));
  };

  const handleClientChange = (opt) => {
    dispatchForm(formActions.productEdited({ client: opt?.value || null }));
  };

  const handleToggleSponsored = (e) => {
    const v = e.target.checked;
    dispatchForm(formActions.productEdited({ sponsored: v }));
  };

  const handleRatingChange = (e) => {
    let val = parseFloat(e.target.value);
    if (Number.isNaN(val)) val = 0;
    if (val < 0) val = 0;
    if (val > 10) val = 10;
    dispatchForm(formActions.productEdited({ rating: val }));
  };

  const handleReceiveImage = (img) => {
    dispatchForm(formActions.productEdited({ productImg: img }));
  };

  const handleRemoveImage = async () => {
    let payload: { client?: unknown; [key: string]: unknown } = {
      _id: id,
      country_code: product.country_code,
      title,
      productImg: "",
      alt_pic,
      rating,
      sponsored,
    };
    if (selectedClientId) payload = { ...payload, client: selectedClientId };
    setLoading(true);
    // Only clear the image locally when the update succeeds (SCRUM-815).
    try {
      await dispatch(updateProduct(payload));
      dispatchForm(formActions.productEdited({ productImg: "" }));
    } catch (err) {
      setLoading(false);
      NotificationAlertOptions(
        "error",
        failureMessage(err, "Removing the image failed. Please try again.")
      );
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    let payload: { client?: unknown; [key: string]: unknown } = {
      _id: id,
      country_code: product.country_code,
      title,
      productImg: product.productImg,
      alt_pic,
      rating,
      sponsored,
      image: product.image,
    };
    // SCRUM-312: `active` is deliberately absent. The server's $set drops an
    // undefined value, so the stored flag is left as it is instead of being
    // rewritten from a form field that no longer exists.
    if (selectedClientId) payload = { ...payload, client: selectedClientId };

    // updateProduct / useImageUpload re-throw on failure (SCRUM-815); surface the
    // error instead of leaving an unhandled rejection.
    try {
      // Linearised (SCRUM-1030): upload via useImageUpload then save inline with the
      // returned URL, instead of stashing pendingUpdate and saving from the shared
      // state.images.url watch-effect (which could fire with a stale URL).
      if (product?.productImg?.data) {
        const path = `/${product.country_code}/PRODUCTS/${product.productImg.imgName}`;
        setLoading(true);
        const uploaded = await uploadImage(product.productImg.data, path);
        await dispatch(updateProduct({ ...payload, productImg: uploaded }));
        return;
      }

      // cloud url already there
      if (productImg?.secure_url) {
        await dispatch(updateProduct({ ...payload, productImg: productImg.secure_url }));
        return;
      }

      // unchanged image string or empty -> just update
      await dispatch(updateProduct(payload));
    } catch (err) {
      setLoading(false);
      NotificationAlertOptions(
        "error",
        failureMessage(err, "Updating the product failed. Please try again.")
      );
    }
  };

  // Link/Unlink to current website
  const handleLinkToggle = async (e) => {
    e.preventDefault();
    if (!product || !currentSite?._id) return;

    const previousProduct = product;
    const productState = { ...product };

    if (!Array.isArray(productState.linked)) {
      productState.linked = [];
    }

    const alreadyLinked = productState.linked.some(
      (linkedSite) => linkedSite.siteid === currentSite._id
    );

    // SCRUM-1447: send only the fields this action needs, not the whole in-memory
    // product. The server assigns every key it receives (server/controllers/product.js,
    // `addProductToSite`), so the page's stale copy of an unrelated field used to
    // overwrite a concurrent change to it — measured on `active`, which is no longer
    // shown anywhere since SCRUM-312 and so moved invisibly.
    //
    // Two fields are here on purpose and must not be trimmed further:
    //   `linked` — the server derives the unlinked sites from the difference between
    //              the stored and the saved array, for the SCRUM-1301 cascade.
    //   `client` — the handler coerces an absent `client` to null and then writes it,
    //              so omitting it would clear the product's client on every link.
    const linkPayload = (linked, version) => ({
      _id: productState._id,
      linked,
      __v: version,
      client: productState.client,
    });

    // Apply the link/unlink optimistically but revert it if patchProduct fails, so the UI
    // no longer diverges from the server on a failed link toggle (SCRUM-815).
    const run = async () => {
    try {
      if (!alreadyLinked) {
        const data = {
          siteid: currentSite._id,
          value: { title: "", clickOutUrl: "", discount: "", listingText: "" },
        };
        productState.linked = [...productState.linked, data];
        dispatchForm(formActions.productReplaced({ ...productState, __v: (productState.__v || 0) + 1 }));
        await dispatch(
          patchProduct(linkPayload(productState.linked, productState.__v))
        );
      } else {
        const filtered = productState.linked.filter(
          (linkedSite) => linkedSite.siteid !== currentSite._id
        );
        const newProd = { ...productState, linked: filtered, __v: (productState.__v || 0) + 1 };
        dispatchForm(formActions.productReplaced(newProd));
        // SCRUM-1448: send the STORED `__v`, not `newProd.__v`. The bump above is for the
        // local copy only — mongoose increments the version itself on a successful save
        // because `linked` is a modified array, so the optimistic state correctly
        // anticipates what the document will hold. Putting that anticipated value in the
        // payload is what broke this: `addProductToSite` assigns every body key onto the
        // loaded document, so `__v` became the version mongoose then keyed its own
        // concurrency check on, no document matched, and the save was refused with
        // `No matching document found … version <n+1>`. Unlink returned 400 and never
        // persisted — and the SCRUM-1301 listing cascade behind it never ran.
        //
        // The link branch above and `LinkedSites.tsx` both already send the stored value;
        // this makes the third path agree with them. Do not "fix" this by moving the bump
        // somewhere else: `953830d2` did exactly that in 2025 and simply swapped which of
        // the two branches was broken.
        await dispatch(patchProduct(linkPayload(newProd.linked, productState.__v)));
      }

      if (id) dispatch(getEntityPendingChange("product", id));
    } catch (err) {
      dispatchForm(formActions.productReplaced(previousProduct));
      NotificationAlertOptions(
        "error",
        failureMessage(err, "Linking the product failed. Please try again.")
      );
    }
    };

    if (!alreadyLinked) return run();

    // SCRUM-1303: an unlink goes behind a confirmation that names the listing
    // pages of this site the SCRUM-1301 cascade will change. The optimistic flip
    // above therefore happens on Confirm, not on the click; Cancel runs nothing.
    // The lookup failing is not "no pages": say so and leave the link in place.
    let pages;
    try {
      pages = await fetchProductListingPages(productState._id, [String(currentSite._id)]);
    } catch {
      NotificationAlertOptions(
        "error",
        "Could not look up the listing pages this product is on. Nothing was changed."
      );
      return;
    }
    setAlert(
      <ConfirmDelete
        title="Unlink this product from the current website?"
        onClose={() => setAlert(null)}
        onConfirm={run}
        showCancel
      >
        <ListingPagesAffected pages={pages} />
      </ConfirmDelete>
    );
  };

  const isLinkedToCurrent =
    !!product?.linked && !!currentSite?._id && product.linked.some((l) => l.siteid === currentSite._id);

  const viewReady = !!product?._id && pendingChangeReady;

  return viewReady ? (
    <>
    {alert}
    <UpdateProductView
      // header & context
      product={product}
      currentSite={currentSite}
      isLinkedToCurrent={isLinkedToCurrent}
      onToggleLink={handleLinkToggle}

      // selects
      countriesOptions={COUNTRY_OPTIONS}
      countryOption={COUNTRY_OPTIONS.find((o) => o.value === product.country_code) || null}
      clientsOptions={clientsOptions}
      selectedClientOpt={clientsOptions.find((o) => o.value === selectedClientId) || null}

      // values
      title={title}
      alt_pic={alt_pic}
      sponsored={sponsored}
      rating={rating}
      productImgDisplay={
        productImg
          ? productImg.secure_url
            ? productImg.secure_url
            : productImg
          : null
      }

      // handlers
      onAltChange={handleAltChange}
      onTitleChange={handleTitleChange}
      onCountryChange={handleCountryChange}
      onClientChange={handleClientChange}
      onToggleSponsored={handleToggleSponsored}
      onRatingChange={handleRatingChange}
      onReceiveImage={handleReceiveImage}
      onRemoveImage={handleRemoveImage}
      onUpdate={handleUpdate}
      entityPendingChange={entityPendingChange}
    />
    </>
  ) : (
    <div>Loading...</div>
  );
};

export default UpdateProductContainer;
